import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, Sun } from "lucide-react";
import AppShell from "@/components/AppShell";
import { getSessionHealth } from "@/lib/api";

// Business-area labels used for grouping the observations returned by
// /api/session/business/health. Only areas the observation engine actually
// classifies are shown — nothing is invented.
const AREA_LABELS = {
  "Contact":     "Phone & website",
  "Category":    "Business category",
  "Reputation":  "Reviews & rating",
  "Content":     "Photos & hours",
  "Identity":    "Business identity",
  "Visibility":  "Search visibility",
  "Operational": "Operational status",
};

const SEVERITY_META = {
  positive:      { icon: CheckCircle2, label: "Looking good",  fg: "var(--ta-success)",  bg: "var(--ta-success-bg)" },
  informational: { icon: CheckCircle2, label: "Recorded",       fg: "var(--ta-primary)",  bg: "var(--ta-accent)" },
  attention:     { icon: AlertCircle,  label: "Worth reviewing", fg: "var(--ta-warning)",  bg: "#FFF3D6" },
};

export default function BusinessHealth() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", data: null, error: null });

  useEffect(() => {
    let alive = true;
    getSessionHealth()
      .then((data) => alive && setState({ status: "ready", data, error: null }))
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status === 401) {
          navigate("/signup", { replace: true });
          return;
        }
        setState({ status: "error", data: null, error: err });
      });
    return () => { alive = false; };
  }, [navigate]);

  return (
    <AppShell showTabBar activeTab="health" testId="health-page">
      <div className="pt-6 sm:pt-10 stagger">
        <div className="text-xs uppercase tracking-widest font-medium mb-2" style={{ color: "var(--ta-secondary)" }}>
          Business Health
        </div>

        {state.status === "loading" && (
          <div className="flex items-center gap-2 text-sm pt-4" style={{ color: "var(--ta-text-secondary)" }} data-testid="health-loading">
            <Loader2 size={16} className="animate-spin" /> Loading your business review…
          </div>
        )}

        {state.status === "error" && (
          <div className="pt-4" data-testid="health-error">
            <div className="font-display text-2xl font-semibold" style={{ color: "var(--ta-primary)" }}>
              We couldn't load this information right now.
            </div>
            <p className="mt-2 text-base" style={{ color: "var(--ta-text-secondary)" }}>
              Please try again shortly.
            </p>
          </div>
        )}

        {state.status === "ready" && state.data.status === "reviewing" && (
          <ReviewingState />
        )}

        {state.status === "ready" && state.data.status === "ready" && (
          <ObservationList observations={state.data.observations} />
        )}
      </div>
    </AppShell>
  );
}

const ReviewingState = () => (
  <div data-testid="health-reviewing">
    <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]" style={{ color: "var(--ta-primary)" }}>
      We're reviewing your business information.
    </h1>
    <p className="mt-3 text-lg leading-relaxed max-w-md" style={{ color: "var(--ta-text-secondary)" }}>
      Anything worth showing here will appear once we've finished reviewing your Google Business Profile.
    </p>
    <div
      className="mt-8 rounded-[32px] p-8 sm:p-10 text-center"
      style={{ background: "var(--ta-success-bg)" }}
      data-testid="health-review-card"
    >
      <div
        className="mx-auto inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
        style={{ background: "var(--ta-success)", color: "#fff" }}
      >
        <Sun size={38} strokeWidth={2.2} />
      </div>
      <div className="font-display text-2xl sm:text-3xl font-semibold" style={{ color: "var(--ta-primary)" }}>
        We're on it.
      </div>
      <p className="mt-2 text-base max-w-md mx-auto" style={{ color: "var(--ta-text-secondary)" }}>
        Your first Monday Brief lands next Monday at 7:00am.
      </p>
    </div>
  </div>
);

const ObservationList = ({ observations }) => {
  const attentionCount = observations.filter((o) => o.severity === "attention").length;
  const positiveCount = observations.filter((o) => o.severity === "positive").length;
  const total = observations.length;

  return (
    <div>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]" style={{ color: "var(--ta-primary)" }}>
        {attentionCount === 0 ? "Everything looks good so far." : "Here's what we're watching."}
      </h1>
      <p className="mt-3 text-lg leading-relaxed max-w-md" style={{ color: "var(--ta-text-secondary)" }} data-testid="health-summary">
        {attentionCount === 0
          ? `We've reviewed ${total} area${total === 1 ? "" : "s"} of your business — nothing needs your attention yet.`
          : `${positiveCount} of ${total} area${total === 1 ? "" : "s"} look healthy. The rest will be covered in your Monday Brief.`}
      </p>

      <div className="mt-8 flex flex-col gap-3" data-testid="health-items">
        {observations.map((o) => {
          const meta = SEVERITY_META[o.severity] || SEVERITY_META.informational;
          const Icon = meta.icon;
          const areaLabel = AREA_LABELS[o.businessArea] || o.businessArea || "General";
          return (
            <div
              key={o.id}
              className="p-5 rounded-2xl bg-white border flex items-center gap-4"
              style={{ borderColor: "var(--ta-border)" }}
              data-testid={`health-item-${o.id}`}
            >
              <div
                className="w-11 h-11 rounded-full inline-flex items-center justify-center shrink-0"
                style={{ background: meta.bg, color: meta.fg }}
              >
                <Icon size={22} strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-base" style={{ color: "var(--ta-primary)" }}>{areaLabel}</div>
                <div className="text-sm mt-0.5" style={{ color: "var(--ta-text-secondary)" }}>{o.observationType || "General"}</div>
              </div>
              <span
                className="text-[11px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full shrink-0"
                style={{ background: meta.bg, color: meta.fg }}
              >
                {meta.label}
              </span>
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-sm text-center" style={{ color: "var(--ta-text-secondary)" }}>
        We recheck every day. You'll only hear from us on Monday.
      </p>
    </div>
  );
};
